import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  passwordLowerCaseRegex,
  passwordNumberRegex,
  passwordSpecialCharRegex,
  passwordUpperCaseRegex
} from '@selfserve/react-utils';
import {
  StyledV2RequirementsContainer,
  StyledV2Requirement,
  StyledV2RequirementIcon,
} from '@selfserve/react-ui';

export interface PasswordRequirements {
  minLength: boolean;
  hasNumber: boolean;
  hasSpecialChar: boolean;
  hasUpperAndLower: boolean;
}

export interface PasswordRequirementsIndicatorProps {
  password: string;
  touched: boolean;
}

export const PasswordRequirementsIndicator: React.FC<
  PasswordRequirementsIndicatorProps
> = ({ password, touched }) => {
  const { t } = useTranslation();
  const requirements: PasswordRequirements = {
    minLength: password.length >= 8,
    hasNumber: passwordNumberRegex.test(password),
    hasSpecialChar: passwordSpecialCharRegex.test(password),
    hasUpperAndLower: passwordLowerCaseRegex.test(password) && passwordUpperCaseRegex.test(password),
  };

  const getIconUrl = (isMet: boolean, isTouched: boolean): string => {
    if (!isTouched) return '/assets/circle.svg';
    return isMet ? '/assets/check.svg' : '/assets/fail.svg';
  };

  return (
    <StyledV2RequirementsContainer>
      <StyledV2Requirement
        hasError={!requirements.minLength}
        touched={touched}
        className={`password-checking ${requirements.minLength ? 'checked' : ''}`}
      >
        <StyledV2RequirementIcon
          src={getIconUrl(requirements.minLength, touched)}
          hasError={!requirements.minLength}
          touched={touched}
        />
        <p>{t('password-requirements.min-characters')}</p>
      </StyledV2Requirement>

      <StyledV2Requirement
        hasError={!requirements.hasNumber}
        touched={touched}
        className={`password-checking ${requirements.hasNumber ? 'checked' : ''}`}
      >
        <StyledV2RequirementIcon
          src={getIconUrl(requirements.hasNumber, touched)}
          hasError={!requirements.hasNumber}
          touched={touched}
        />
        <p>{t('password-requirements.number')}</p>
      </StyledV2Requirement>

      <StyledV2Requirement
        hasError={!requirements.hasSpecialChar}
        touched={touched}
        className={`password-checking ${requirements.hasSpecialChar ? 'checked' : ''}`}
      >
        <StyledV2RequirementIcon
          src={getIconUrl(requirements.hasSpecialChar, touched)}
          hasError={!requirements.hasSpecialChar}
          touched={touched}
        />
        <p>{t('password-requirements.special-char')}</p>
      </StyledV2Requirement>

      <StyledV2Requirement
        hasError={!requirements.hasUpperAndLower}
        touched={touched}
        className={`password-checking ${requirements.hasUpperAndLower ? 'checked' : ''}`}
      >
        <StyledV2RequirementIcon
          src={getIconUrl(requirements.hasUpperAndLower, touched)}
          hasError={!requirements.hasUpperAndLower}
          touched={touched}
        />
        <p>{t('password-requirements.lowercase-uppercase')}</p>
      </StyledV2Requirement>
    </StyledV2RequirementsContainer>
  );
};
